/**
 * Frontend Application - Alpine.js
 */

import "./bootstrap";
import Alpine from "alpinejs";

// Make Alpine available globally
window.Alpine = Alpine;

// Register Alpine data components

// Mobile Menu Component
Alpine.data("mobileMenu", () => ({
    open: false,
    toggle() {
        this.open = !this.open;
    },
}));

// Scripture Modal Component
Alpine.data("scriptureModal", () => ({
    open: false,
    passage: "",
    passageHeader: "",

    async fetchScripture(verse) {
        try {
            const response = await axios.post("/api/scripture", {
                passage: verse,
            });
            this.passage = response.data.passages.join("");
            this.passageHeader = response.data.query;
            this.open = true;
        } catch (error) {
            console.error("Error fetching scripture:", error);
        }
    },

    close() {
        this.open = false;
    },
}));

// Audio Player Component
Alpine.data("audioPlayer", () => ({
    playing: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    audioEl: null,

    init() {
        this.audioEl = this.$refs.audio;
        if (this.audioEl) {
            this.audioEl.addEventListener("loadedmetadata", () => {
                this.duration = this.audioEl.duration;
            });
            this.audioEl.addEventListener("timeupdate", () => {
                this.currentTime = this.audioEl.currentTime;
            });
            this.audioEl.addEventListener("ended", () => {
                this.playing = false;
            });
        }
    },

    togglePlay() {
        if (this.playing) {
            this.audioEl.pause();
        } else {
            this.audioEl.play();
        }
        this.playing = !this.playing;
    },

    seek(e) {
        const rect = e.target.getBoundingClientRect();
        const percent = (e.clientX - rect.left) / rect.width;
        this.audioEl.currentTime = percent * this.duration;
    },

    formatTime(seconds) {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    },
}));

// Sermon Player Component (for sermon single page)
Alpine.data("sermonPlayer", () => ({
    showPlayer: false,
    playing: false,
    currentTime: 0,
    duration: 0,

    openPlayer() {
        this.showPlayer = true;
        this.$nextTick(() => {
            if (this.$refs.audio) {
                this.$refs.audio.play();
                this.playing = true;
            }
        });
    },

    closePlayer() {
        if (this.$refs.audio) {
            this.$refs.audio.pause();
        }
        this.playing = false;
        this.showPlayer = false;
    },

    togglePlay() {
        if (this.$refs.audio) {
            if (this.playing) {
                this.$refs.audio.pause();
            } else {
                this.$refs.audio.play();
            }
            this.playing = !this.playing;
        }
    },

    seek(e) {
        if (this.$refs.audio) {
            const rect = e.currentTarget.getBoundingClientRect();
            const percent = (e.clientX - rect.left) / rect.width;
            this.$refs.audio.currentTime = percent * this.duration;
        }
    },

    formatTime(seconds) {
        if (!seconds || isNaN(seconds)) return "0:00";
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    },
}));

// Subscription Form Component
Alpine.data("subscriptionForm", () => ({
    first_name: "",
    last_name: "",
    email: "",
    website: "", // honeypot: people never see it, form-filling bots fill it
    startedAt: Date.now(),
    subscribed: false,
    error: false,
    errorMessage: "",
    loading: false,

    async subscribe() {
        this.error = false;
        this.errorMessage = "";

        if (!this.email.length) {
            this.error = true;
            this.errorMessage = "Please enter a valid email address";
            return;
        }

        this.loading = true;

        try {
            await axios.post("/subscriber/new", {
                first_name: this.first_name,
                last_name: this.last_name,
                email: this.email,
                website: this.website,
                elapsed_ms: Date.now() - this.startedAt,
            });
            this.subscribed = true;
        } catch (error) {
            if (error.response?.status === 422) {
                this.errorMessage = error.response.data.errors.email[0];
            } else {
                this.errorMessage = "An error occurred. Please try again.";
            }
            this.error = true;
        } finally {
            this.loading = false;
        }
    },
}));

// Unsubscription Form Component
Alpine.data("unsubscriptionForm", () => ({
    email: "",
    unsubscribed: false,
    error: false,
    errorMessage: "",
    loading: false,

    async unsubscribe() {
        this.error = false;
        this.errorMessage = "";

        if (!this.email.length) {
            this.error = true;
            this.errorMessage = "Please enter your email address";
            return;
        }

        this.loading = true;

        try {
            await axios.post("/subscriber/remove", { email: this.email });
            this.unsubscribed = true;
        } catch (error) {
            this.errorMessage = "An error occurred. Please try again.";
            this.error = true;
        } finally {
            this.loading = false;
        }
    },
}));

// Donate Form Component
Alpine.data("donateForm", () => ({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    amount: 50,
    recurring: true,
    error: false,
    errorMessage: "",
    submitting: false,
    donationComplete: false,
    receipt: null,
    stripe: null,
    elements: null,
    card: null,
    cardError: "",
    stripeKey: "",

    async init() {
        // Load Stripe.js
        if (!window.Stripe) {
            const script = document.createElement("script");
            script.src = "https://js.stripe.com/v3/";
            script.onload = () => this.initStripe();
            document.head.appendChild(script);
        } else {
            this.initStripe();
        }

        // Get Stripe key
        try {
            const response = await axios.get("/api/stripe/key");
            this.stripeKey = response.data.key;
        } catch (error) {
            console.error("Error getting Stripe key:", error);
        }
    },

    initStripe() {
        if (!this.stripeKey) {
            setTimeout(() => this.initStripe(), 500);
            return;
        }

        this.stripe = Stripe(this.stripeKey);
        this.elements = this.stripe.elements();

        const style = {
            base: {
                color: "#32325d",
                lineHeight: "18px",
                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                fontSmoothing: "antialiased",
                fontSize: "16px",
                "::placeholder": {
                    color: "#aab7c4",
                },
            },
            invalid: {
                color: "#fa755a",
                iconColor: "#fa755a",
            },
        };

        this.card = this.elements.create("card", { style });
        this.card.mount("#card-element");

        this.card.on("change", (event) => {
            this.cardError = event.error ? event.error.message : "";
        });
    },

    setAmount(amount) {
        this.amount = amount;
    },

    validateForm() {
        if (!this.firstName || !this.lastName || !this.email || !this.amount) {
            this.error = true;
            this.errorMessage = "Please fill in all required fields";
            return false;
        }
        return true;
    },

    async donate() {
        if (!this.validateForm()) return;

        this.submitting = true;
        this.error = false;
        this.errorMessage = "";

        const cardHolderName = `${this.firstName} ${this.lastName}`;

        try {
            const { paymentMethod, error } =
                await this.stripe.createPaymentMethod("card", this.card, {
                    billing_details: { name: cardHolderName },
                });

            if (error) {
                this.error = true;
                this.errorMessage = error.message;
                this.submitting = false;
                return;
            }

            const response = await axios.post("/api/donation/new", {
                paymentMethod: paymentMethod.id,
                name: cardHolderName,
                address: this.address,
                phone: this.phone,
                email: this.email,
                amount: this.amount,
                recurring: Boolean(this.recurring),
            });

            this.receipt = response.data.receipt;
            localStorage.setItem("donateModal", "true");
            // Send donors to the thank-you/conversion page. GA4 'purchase' fires there
            // once per gift (deduped), so we no longer fire an event here.
            if (response.data.thank_you_url) {
                window.location.assign(response.data.thank_you_url);
                return;
            }
            this.donationComplete = true;
        } catch (error) {
            this.error = true;
            this.errorMessage =
                error.response?.data?.message ||
                "Payment failed. Please try again.";
        } finally {
            this.submitting = false;
        }
    },
}));

// Comments Component
Alpine.data("comments", (postId) => ({
    comments: [],
    newComment: "",
    loading: false,

    async init() {
        await this.loadComments();
    },

    async loadComments() {
        try {
            const response = await axios.get("/api/post/comments", {
                params: { parent_id: postId },
            });
            this.comments = response.data;
        } catch (error) {
            console.error("Error loading comments:", error);
        }
    },

    async submitComment() {
        if (!this.newComment.trim()) return;

        this.loading = true;

        try {
            await axios.post("/api/comment/new", {
                parent_id: postId,
                parent_type: "post",
                comment: this.newComment,
            });
            this.newComment = "";
            await this.loadComments();
        } catch (error) {
            console.error("Error submitting comment:", error);
        } finally {
            this.loading = false;
        }
    },
}));

// Search Modal Component
Alpine.data("searchModal", () => ({
    open: false,
    query: "",

    openModal() {
        this.open = true;
        this.$nextTick(() => {
            this.$refs.searchInput?.focus();
        });
    },

    closeModal() {
        this.open = false;
        this.query = "";
    },

    search() {
        if (this.query.trim()) {
            window.location.href = `/search?q=${encodeURIComponent(
                this.query
            )}`;
        }
    },
}));

// Toast Notification Component
Alpine.data("toast", () => ({
    show: false,
    message: "",
    type: "success",

    showToast(message, type = "success", duration = 5000) {
        this.message = message;
        this.type = type;
        this.show = true;

        setTimeout(() => {
            this.show = false;
        }, duration);
    },
}));

// Donate Modal Component (prompts users to donate)
Alpine.data("donateModal", () => ({
    show: false,

    init() {
        // Show modal after 30 seconds if user hasn't donated
        if (!localStorage.getItem("donateModal")) {
            setTimeout(() => {
                this.show = true;
            }, 30000);
        }
    },

    close() {
        this.show = false;
        localStorage.setItem("donateModal", "true");
    },
}));

// Registration Form Component
Alpine.data("registrationForm", () => ({
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    message: "",
    errors: false,
    fieldErrors: {
        name: "",
        email: "",
        password: "",
    },
    submitting: false,

    removeError(field) {
        this.fieldErrors[field] = "";
        this.errors = false;
    },

    async signUp() {
        this.submitting = true;
        this.errors = false;
        this.fieldErrors = { name: "", email: "", password: "" };

        try {
            await axios.post("/register", {
                name: this.name,
                email: this.email,
                password: this.password,
                password_confirmation: this.password_confirmation,
            });
            window.location.href = "/home";
        } catch (error) {
            this.errors = true;
            this.message =
                error.response?.data?.message || "Registration failed";

            const errorData = error.response?.data?.errors || {};
            if (errorData.name) this.fieldErrors.name = errorData.name[0];
            if (errorData.email) this.fieldErrors.email = errorData.email[0];
            if (errorData.password)
                this.fieldErrors.password = errorData.password[0];
        } finally {
            this.submitting = false;
        }
    },
}));

// Initialize Alpine
Alpine.start();

// Lazy load background images using IntersectionObserver
document.addEventListener("DOMContentLoaded", () => {
    const lazyBgElements = document.querySelectorAll(".lazy-bg[data-bg]");

    if ("IntersectionObserver" in window) {
        const bgObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const el = entry.target;
                        const bgUrl = el.dataset.bg;
                        if (bgUrl) {
                            el.style.backgroundImage = `url('${bgUrl}')`;
                            el.classList.add("bg-loaded");
                        }
                        bgObserver.unobserve(el);
                    }
                });
            },
            { rootMargin: "50px 0px" }
        );

        lazyBgElements.forEach((el) => bgObserver.observe(el));
    } else {
        // Fallback for browsers without IntersectionObserver
        lazyBgElements.forEach((el) => {
            const bgUrl = el.dataset.bg;
            if (bgUrl) {
                el.style.backgroundImage = `url('${bgUrl}')`;
            }
        });
    }
});
